import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Phone, Mail, MapPin, Clock, ArrowRight, MessageCircle } from 'lucide-react';

const WA_NUMBER = '971581735200';

const SERVICES = [
    { label: 'Marble Polishing', slug: 'marble-polishing' },
    { label: 'Tile & Marble Installation', slug: 'tile-marble-installation' },
    { label: 'Ceiling Works', slug: 'ceiling-works' },
    { label: 'Joinery & Carpentry', slug: 'joinery-carpentry' },
];

const QUICK_LINKS = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Contact', href: '/contact' },
];

const SOCIALS = [
    { icon: <Facebook size={18} />, href: 'https://facebook.com', label: 'Facebook' },
    { icon: <Instagram size={18} />, href: 'https://instagram.com', label: 'Instagram' },
    { icon: <Linkedin size={18} />, href: 'https://linkedin.com', label: 'LinkedIn' },
];

const Footer = () => (
    <footer style={{ background: '#0a0a0a', borderTop: '1px solid rgba(212,175,55,0.15)' }}>

        {/* ── Gold glow strip ── */}
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent 0%, #D4AF37 30%, #fff8dc 50%, #D4AF37 70%, transparent 100%)' }} />

        {/* ── CTA Banner ── */}
        <div
            className="w-full py-10 px-6 text-center"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.03) 100%)', borderBottom: '1px solid rgba(212,175,55,0.08)' }}
        >
            <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-3" style={{ color: '#D4AF37' }}>
                Ready to Transform Your Space?
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 leading-tight">
                Let's Build Something <em className="not-italic" style={{ color: '#D4AF37' }}>Extraordinary</em>
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                    href={`https://wa.me/${WA_NUMBER}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-white text-sm font-semibold transition-all duration-300 hover:brightness-110"
                    style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}
                >
                    <MessageCircle size={16} /> WhatsApp Us
                </a>
                <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3 text-sm font-semibold uppercase tracking-widest border transition-all duration-300 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37]"
                    style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                >
                    Get a Free Quote <ArrowRight size={14} />
                </Link>
            </div>
        </div>

        {/* ── Main Footer Grid ── */}
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

            {/* Brand column */}
            <div className="lg:col-span-1">
                {/* ROYAL [logo] STANDARD */}
                <Link to="/" className="inline-flex items-center gap-2 mb-5">
                    <span className="font-serif text-2xl font-bold text-white tracking-widest">ROYAL</span>
                    <img
                        src="/logo.png"
                        alt="Royal Standard Logo"
                        style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
                    />
                    <span className="font-serif text-2xl font-bold tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                </Link>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    Dubai's premier home finishing specialists. Elevating interiors with marble, tile, bespoke joinery and architectural ceiling works since 2014.
                </p>
                {/* Socials */}
                <div className="flex gap-3">
                    {SOCIALS.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                            style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#D4AF37'; e.currentTarget.style.color = '#000'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.1)'; e.currentTarget.style.color = '#D4AF37'; }}
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
            </div>

            {/* Services */}
            <div>
                <h4
                    className="text-white font-serif text-lg mb-6 pb-3"
                    style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}
                >
                    Our Services
                </h4>
                <ul className="space-y-3">
                    {SERVICES.map((s) => (
                        <li key={s.slug}>
                            <Link
                                to={`/services/${s.slug}`}
                                className="flex items-center gap-2 text-gray-500 text-sm transition-all duration-200 hover:text-white group"
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-200 group-hover:scale-150"
                                    style={{ background: '#D4AF37' }}
                                />
                                {s.label}
                            </Link>
                        </li>
                    ))}
                </ul>
                <h4
                    className="text-white font-serif text-lg mt-8 mb-6 pb-3"
                    style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}
                >
                    Quick Links
                </h4>
                <ul className="space-y-3">
                    {QUICK_LINKS.map((l) => (
                        <li key={l.label}>
                            <Link
                                to={l.href}
                                className="flex items-center gap-2 text-gray-500 text-sm transition-all duration-200 hover:text-white group"
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-200 group-hover:scale-150"
                                    style={{ background: '#D4AF37' }}
                                />
                                {l.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Contact Info */}
            <div>
                <h4
                    className="text-white font-serif text-lg mb-6 pb-3"
                    style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}
                >
                    Contact Us
                </h4>
                <ul className="space-y-5">
                    {[
                        { icon: <Phone size={16} />, label: '+971 58 173 5200', href: 'tel:+971581735200' },
                        { icon: <Mail size={16} />, label: 'info@royalstandarduae.com', href: 'mailto:info@royalstandarduae.com' },
                        { icon: <MapPin size={16} />, label: 'Dubai, United Arab Emirates', href: null },
                        { icon: <Clock size={16} />, label: 'Mon – Sat: 8:00 AM – 7:00 PM', href: null },
                    ].map((c, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span
                                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
                                style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}
                            >
                                {c.icon}
                            </span>
                            {c.href ? (
                                <a href={c.href} className="text-gray-400 text-sm hover:text-white transition-colors leading-relaxed">
                                    {c.label}
                                </a>
                            ) : (
                                <span className="text-gray-400 text-sm leading-relaxed">{c.label}</span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            {/* WhatsApp CTA card */}
            <div>
                <h4
                    className="text-white font-serif text-lg mb-6 pb-3"
                    style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}
                >
                    Chat Instantly
                </h4>
                <div
                    className="rounded-sm p-6 flex flex-col items-center text-center gap-4"
                    style={{ background: 'linear-gradient(135deg, rgba(18,140,126,0.12), rgba(37,211,102,0.1))', border: '1px solid rgba(37,211,102,0.25)' }}
                >
                    <div
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: '#25D366', boxShadow: '0 6px 20px rgba(37,211,102,0.35)' }}
                    >
                        <MessageCircle size={26} color="white" />
                    </div>
                    <div>
                        <p className="text-white text-sm font-semibold mb-1">We're on WhatsApp</p>
                        <p className="text-gray-500 text-xs leading-relaxed">
                            Describe your project and get a response within 60 minutes.
                        </p>
                    </div>
                    <a
                        href={`https://wa.me/${WA_NUMBER}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-white text-sm font-semibold transition-all duration-300 hover:brightness-110"
                        style={{ background: '#25D366' }}
                    >
                        <MessageCircle size={15} /> Start Chat
                    </a>
                </div>

                {/* Certifications / Trust badges */}
                <div
                    className="mt-5 rounded-sm px-4 py-3 flex items-center justify-center gap-3"
                    style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.12)' }}
                >
                    <span className="text-yellow-500 text-lg">★★★★★</span>
                    <span className="text-gray-500 text-xs leading-snug">5-Star rated<br />Premium Service · UAE</span>
                </div>
            </div>
        </div>

        {/* ── Gold divider ── */}
        <div className="max-w-7xl mx-auto px-6">
            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)' }} />
        </div>

        {/* ── Bottom bar ── */}
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-xs tracking-widest uppercase">
                © {new Date().getFullYear()} Royal Standard. All rights reserved.
            </p>
            <div className="flex gap-6 text-gray-600 text-xs uppercase tracking-widest">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
            <p className="text-gray-700 text-xs">
                Est. 2014 · Dubai, UAE 🇦🇪
            </p>
        </div>

    </footer>
);

export default Footer;
